import {
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Switch,
} from "@heroui/react";
import { useEffect, useState } from "react";
import {
  ContractMilestoneDate,
  ContactType,
  MilestoneDateType,
  ContractContact,
} from "shared/types/rental-contracts";
import { UnitDTO } from "shared/types/unit";
import { Handshake } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { formatDate } from "shared/utils/get-dates";
import { applyMask } from "shared/utils/masks";
import { UnitIdLinkChip } from "../components/UnitIdLinkChip";

export interface AlmostRentedUnitsTableProps {
  almostRentedUnits: UnitDTO[];
  fetchAlmostRentedUnits: () => Promise<void>;
  fetchUnitsInRenovation: () => Promise<void>;
}

const AlmostRentedUnitsTable = ({ 
  almostRentedUnits, 
  fetchAlmostRentedUnits, 
  fetchUnitsInRenovation 
}: AlmostRentedUnitsTableProps) => {
  const [units, setUnits] = useState<UnitDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { session } = useSession();

  useEffect(() => {
    setUnits(almostRentedUnits || []);
    setIsLoading(false);
  }, [almostRentedUnits]);

  // Helper function to determine the potential tenant for a unit
  const getPotentialTenant = (unit: UnitDTO) => {
    if (unit.contracts && Array.isArray(unit.contracts)) {
      // Find contract in concept phase
      const conceptContract = unit.contracts.find(
        contract => contract.phase === "In concept"
      );

      if (conceptContract?.contractContacts) {
        const tenantContacts = conceptContract.contractContacts.filter(
          (contact: ContractContact) => contact.contactType === ContactType.TENANT
        );

        if (tenantContacts.length > 0) {
          return tenantContacts.map((contact: ContractContact) => contact.resource?.name).join(", ");
        }
      }
    }

    return "Geen huurder gevonden";
  };

  // Helper function to determine the start date for a unit
  const getStartDate = (unit: UnitDTO) => {
    if (unit.contracts && Array.isArray(unit.contracts)) {
      // Find contract in concept phase
      const conceptContract = unit.contracts.find(
        contract => contract.phase === "In concept"
      );

      if (conceptContract?.contractMilestoneDates) {
        const startDates = conceptContract.contractMilestoneDates.filter(
          (milestone: ContractMilestoneDate) =>
            milestone.milestoneDateType === MilestoneDateType.START_DATE &&
            milestone.date
        );

        if (startDates.length > 0) {
          return formatDate(new Date(startDates[0].date!));
        }
      }
    }

    return "Geen startdatum gevonden";
  };

  const calculateERVPerM2 = (unit: UnitDTO) => {
    if (!unit.estimatedRentalValue || !unit.netSurface) return "N/A";
    return `€ ${(unit.estimatedRentalValue / unit.netSurface).toFixed(2)}`;
  };

  const handleUpdateUnit = async (unitId: string, updates: any) => {
    try {
      await fetchWithAuth(
        `${API_URL}/units/update/${unitId}`,
        {
          method: "PUT",
          body: JSON.stringify({ id: unitId, ...updates }),
          headers: {
            'Content-Type': 'application/json'
          }
        },
        session
      );
      // If the renovation status changed, refresh all related tables
      if ('vacantDueToRenovation' in updates) {
        // Fetch data for all tables that share units
        fetchAlmostRentedUnits();
        fetchUnitsInRenovation();
      } else {
        // Otherwise just refresh this table
        fetchAlmostRentedUnits();
      }
    } catch (error) {
      console.error("Failed to update unit:", error);
    }
  };

  return (
    <div id="units-almost-rented" className="col-span-full flex flex-col gap-2">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-lg font-medium">
          <Handshake weight="duotone" size={20} />
          Bijna verhuurde units
        </div>
      </header>
      <div className="flex flex-col rounded-lg bg-white shadow-sm">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner
              label="Gegevens aan het ophalen"
              color="success"
              size="md"
            />
          </div>
        ) : units.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="bg-secondary/40 p-4 rounded-full mb-3">
              <Handshake weight="duotone" size={36} className="text-moss-primary" />
            </div>
            <p className="text-neutral-primary font-medium text-base mb-1">Geen nieuwe contracten gevonden</p>
            <p className="text-neutral-secondary-mid text-sm text-center max-w-md">
              Er zijn momenteel geen units met contracten in concept fase. Nieuwe contracten zullen hier verschijnen.
            </p>
          </div>
        ) : (
          <Table
            aria-label="Bijna verhuurde units"
            classNames={{
              wrapper: "shadow-none rounded-lg p-0",
              thead: "h-fit border-b-2 border-neutral-primary/40",
              th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
              tbody: "border-b divide-y divide-neutral-primary/15",
              tr: "divide-x divide-neutral-primary/15",
              td: "py-2.5",
            }}
          >
            <TableHeader>
              <TableColumn>Unit</TableColumn>
              <TableColumn>Potentiële huurder</TableColumn>
              <TableColumn>Startdatum</TableColumn>
              <TableColumn>In renovatie?</TableColumn>
              <TableColumn>Huidige ERV</TableColumn>
              <TableColumn>ERV/m²</TableColumn>
              <TableColumn>Provisies</TableColumn>
            </TableHeader>
            <TableBody>
              {units.map((unit: UnitDTO) => (
                <TableRow key={unit.id}>
                  <TableCell>
                    <UnitIdLinkChip
                      unitId={unit.id as string}
                      unitType={unit.unitType}
                      unitSubtype={unit.unitSubtype}
                    />
                  </TableCell>
                  <TableCell>{getPotentialTenant(unit)}</TableCell>
                  <TableCell>{getStartDate(unit)}</TableCell>
                  <TableCell>
                    <Switch
                      isSelected={unit.vacantDueToRenovation}
                      onValueChange={(value) => {
                        handleUpdateUnit(unit.id as string, {
                          vacantDueToRenovation: value,
                        });
                      }}
                    />
                  </TableCell>
                  <TableCell>{applyMask(unit.estimatedRentalValue, "valuta")}</TableCell>
                  <TableCell>{calculateERVPerM2(unit)}</TableCell>
                  <TableCell>{applyMask(unit.estimatedCommonCosts, "valuta")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default AlmostRentedUnitsTable; 